import React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { RootState } from "configureStore";
import { dayOrNow, formatWeekdayAndDate, useUiLanguage } from "../utils/newsDates";
import { TOUCH_TARGET } from "./touchTargets";

interface NewsHeaderProps {
  /** The server's day ('YYYY-MM-DD') once the sidebar has loaded. */
  today?: string;
  /** How many colleagues are away today; shown when above 0. */
  absentCount: number;
  /** How many colleagues have a birthday today; shown when above 0. */
  birthdayCount: number;
  onRefresh: () => void;
  /** The feed filter, when the company is in a group. */
  children?: React.ReactNode;
}

const greetingKeyForHour = (hour: number) => {
  if (hour < 12) return "news_greeting_morning";
  if (hour < 18) return "news_greeting_day";
  return "news_greeting_evening";
};

/** "Good morning, Ona" by the viewer's local hour, then the date, who is away today and whose birthday it is. */
export function NewsHeader({ today, absentCount, birthdayCount, onRefresh, children }: NewsHeaderProps) {
  const { t } = useTranslation();
  const language = useUiLanguage();
  const { session } = useSelector((state: RootState) => state.authReducer);

  const greeting = t(greetingKeyForHour(new Date().getHours()));
  const firstName = (session?.name ?? "").trim().split(/\s+/)[0];

  const date = formatWeekdayAndDate(dayOrNow(today), language);
  const counts: string[] = [];
  if (absentCount > 0) counts.push(t("news_absent_summary", { count: absentCount }));
  if (birthdayCount > 0) counts.push(t("news_birthday_summary", { count: birthdayCount }));

  return (
    <div className="mb-6">
      <div className="mb-1 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{firstName ? `${greeting}, ${firstName}` : greeting}</h1>
        <button
          onClick={onRefresh}
          className={`flex items-center justify-center text-gray-600 transition-colors hover:text-gray-900 ${TOUCH_TARGET}`}
          title={t("feed_refresh")}
        >
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>
      <p className="text-gray-600" data-testid="news-header-summary">
        <span className="font-medium text-gray-700">{date}</span>
        {counts.length > 0 && ` ${counts.join(" · ")}`}
      </p>
      {children && <div className="mt-3">{children}</div>}
    </div>
  );
}
